import React, { useEffect } from "react";
import * as Accordion from "@radix-ui/react-accordion";
import { ChevronDown, ChevronUp, AlertCircle, CheckCircle } from "lucide-react";
import { Card } from "../ui/card";
import { SectionStatus } from "./types";
import { useFormStore } from "./state";
import { useFormContext } from "react-hook-form";
import { EptForm } from "../form-builder/utils/types";
import { useShallow } from "zustand/react/shallow";
type SectionContainerProps = {
  title: string;
  sectionId: string;
  readonly?: boolean;
  formData?: EptForm;
};
function SectionContainer({
  children,
  title,
  sectionId,
  readonly,
  formData
}: React.PropsWithChildren<SectionContainerProps>) {
  const form = useFormContext();
  const [open, setOpen] = React.useState(true);
  const [scrollElementId, setScrollElementId] = React.useState<string | null>(null);
  const status: SectionStatus = useFormStore(state => state.sectionStatuses[sectionId] || "incomplete");
  const sectionsStatuses = useFormStore(useShallow(state => state.sectionStatuses));
  useEffect(() => {
    if (scrollElementId) {
      const element = document.getElementById(scrollElementId);
      element?.scrollIntoView({
        behavior: "smooth",
        block: "center"
      });
      element?.focus();
      setScrollElementId(null);
    }
  }, [scrollElementId]);
  useEffect(() => {
    if (status === "has-errors") {
      if (open) {
        return;
      }
      setOpen(true);
      const firstSectionIdWithErrors = formData?.sections.find(section => sectionsStatuses[section.sectionId!] === "has-errors");
      if (firstSectionIdWithErrors?.sectionId !== sectionId) {
        return;
      }
      const section = formData?.sections.find(section => section.sectionId === sectionId);
      const firstElementWithError = section?.section.elements.find(element => {
        return form.formState.errors[element.id!];
      });
      if (firstElementWithError) {
        setScrollElementId(firstElementWithError.id!);
      }
    }
  }, [status, sectionId, open, form.formState.errors, formData?.sections, sectionsStatuses]);
  const statusStyles = {
    incomplete: {
      color: "bg-primary",
      borderColor: null,
      icon: open ? <ChevronUp size={16} /> : <ChevronDown size={16} />
    },
    "has-errors": {
      color: "bg-red-500",
      borderColor: "border-red-500",
      icon: <AlertCircle size={16} />
    },
    completed: {
      color: "bg-green-600",
      borderColor: "border-green-600",
      icon: <CheckCircle size={16} />
    }
  };
  const {
    color,
    icon,
    borderColor
  } = statusStyles[status];
  const collapsible = (() => {
    if (readonly) return false;
    if (status === "has-errors") return false;
    return true;
  })();
  return <Card className={`h-full mobile:overflow-hidden ${borderColor && !readonly ? `border-l-4 ${borderColor}` : ""}`} data-sentry-element="Card" data-sentry-component="SectionContainer" data-sentry-source-file="SectionContainer.tsx">
      <Accordion.Root type="single" defaultValue={title} value={open ? title : ""} collapsible={collapsible} onValueChange={value => {
      return setOpen(value === title);
    }} data-sentry-element="unknown" data-sentry-source-file="SectionContainer.tsx">
        <Accordion.Item id={sectionId} value={title} className="my-3 w-full" data-sentry-element="unknown" data-sentry-source-file="SectionContainer.tsx">
          <Accordion.Header className="cursor-pointer" data-sentry-element="unknown" data-sentry-source-file="SectionContainer.tsx">
            <Accordion.Trigger className="flex w-full items-center justify-between gap-4 px-4 py-2 font-bold text-primary outline-none transition-all print:px-1" data-sentry-element="unknown" data-sentry-source-file="SectionContainer.tsx">
              {!readonly && <span className={`flex h-8 w-8 items-center justify-center rounded-full ${color} text-primary-foreground print:hidden`}>
                  {icon}
                </span>}
              <span className="flex-1 text-left text-xs uppercase tracking-widest print:text-base">
                {title}
              </span>
            </Accordion.Trigger>
          </Accordion.Header>
          <Accordion.Content className="overflow-hidden rounded-b-md p-4 transition-all duration-300 ease-in-out print:p-1" data-sentry-element="unknown" data-sentry-source-file="SectionContainer.tsx">
            {children}
          </Accordion.Content>
        </Accordion.Item>
      </Accordion.Root>
    </Card>;
}
export default SectionContainer;